<template>
  <div class="benchmark">
    <!-- 顶部操作栏 -->
    <el-card class="head-card" shadow="never">
      <div class="head-row">
        <div class="head-left">
          <h3>🎯 对标账号分解</h3>
          <p class="sub">录入竞品抖音号 → 采集爆款 → 拆解分析 → 一键入库自动洗稿剪辑发布</p>
        </div>
        <el-button type="primary" @click="openAdd">＋ 添加对标账号</el-button>
      </div>
    </el-card>

    <!-- 对标账号列表 -->
    <el-row :gutter="12" v-if="accounts.length">
      <el-col :xs="24" :sm="12" :md="8" v-for="a in accounts" :key="a.id">
        <el-card class="acc-card" shadow="hover">
          <div class="acc-head">
            <el-avatar :size="40" :src="a.avatar || ''">{{ (a.nickname || '竞品')[0] }}</el-avatar>
            <div class="acc-info">
              <div class="acc-name">{{ a.nickname || '未命名对标' }}</div>
              <div class="acc-meta">
                粉丝 <b>{{ a.fans }}</b> · 作品 <b>{{ a.works_count }}</b>
                <el-tag size="small" :type="a.status === 'active' ? 'success' : 'info'" style="margin-left:6px">
                  {{ a.status === 'active' ? '采集中' : '已暂停' }}
                </el-tag>
              </div>
              <div class="acc-owner" style="font-size:12px;color:#909399;margin-top:4px">
                归属: {{ ownerName(a.account_id) }}
                <el-tag v-if="ownerOnline(a.account_id)" size="mini" type="success" style="margin-left:4px">✅ 在线</el-tag>
                <el-tag v-else size="mini" type="danger" style="margin-left:4px">⚠️ 未登录</el-tag>
              </div>
            </div>
          </div>
          <div class="acc-url">{{ a.profile_url }}</div>
          <div class="acc-foot">
            <span class="last">上次采集: {{ a.last_collected_at ? fmt(a.last_collected_at) : '未采集' }}</span>
          </div>
          <div class="acc-btns">
            <el-button size="small" type="primary" @click="openVideos(a)">👁 查看作品</el-button>
            <el-button size="small" type="warning" @click="analyze(a)">📊 拆解</el-button>
            <el-button size="small" type="danger" plain @click="remove(a)">🗑</el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>
    <el-empty v-else description="还没有对标账号，点右上角添加竞品抖音号（支持直接粘贴抖音分享文本）→ 保存后自动采集作品" />

    <!-- 添加对话框 -->
    <el-dialog v-model="addVisible" title="添加对标账号" width="480px">
      <el-form label-width="90px">
        <el-form-item label="执行账号">
          <el-select v-model="form.account_id" placeholder="用哪个自家账号采集" style="width:100%">
            <el-option v-for="ac in accountsList" :key="ac.id" :value="ac.id">
              <span>{{ ac.nickname }}（#{{ ac.id }}）</span>
              <el-tag v-if="ac.is_logged_in" size="small" type="success" style="margin-left:6px">✅ 已登录</el-tag>
              <el-tag v-else size="small" type="info" style="margin-left:6px">⚠️ 未登录</el-tag>
            </el-option>
          </el-select>
          <div class="acc-hint" style="margin-top:6px;font-size:12px;line-height:1.6">
            <span v-if="form.account_id && curAcct">
              <b style="color:#e6a23c">采集将使用「{{ curAcct.nickname }}」的登录态</b>
              <el-tag v-if="curAcct.is_logged_in" size="mini" type="success" style="margin-left:4px">✅ 在线</el-tag>
              <el-tag v-else size="mini" type="danger" style="margin-left:4px">⚠️ 未登录，采集会失败！</el-tag>
            </span>
            <span v-else style="color:#909399">请选择自家抖音账号（需要先登录）</span>
          </div>
        </el-form-item>
        <el-form-item label="主页链接">
          <el-input v-model="form.profile_url" placeholder="https://www.douyin.com/user/... 或搜索账号名" />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="form.nickname" placeholder="竞品账号昵称（可选）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>

    <!-- 作品抽屉 -->
    <el-drawer v-model="videoDrawer" :title="`🎬 ${currentAccount?.nickname || ''} 的作品（共${videos.length}个 · 按点赞排序）`" :size="isMobile ? '100%' : '62%'">
      <div class="drawer-head">
        <el-button size="small" type="primary" :loading="collectingId === currentAccount?.id" @click="collect(currentAccount)">🔄 重新采集</el-button>
        <span class="hint">⬇下载后可一键入库（自动洗稿+剪辑+复刻）</span>
      </div>
      <!-- PC：表格 -->
      <el-table v-if="!isMobile" :data="videos" v-loading="vLoading" size="small" max-height="62vh">
        <template #empty>
          <div style="padding:24px;text-align:center">
            <div style="font-size:30px;margin-bottom:8px">📭</div>
            <div>暂无作品数据 — 点下方按钮立即采集（约30-60秒）</div>
            <el-button size="small" type="primary" style="margin-top:10px" :loading="vLoading" @click="collect(currentAccount)">🔄 立即采集</el-button>
          </div>
        </template>
        <el-table-column label="封面" width="70">
          <template #default="{ row }">
            <el-image :src="row.cover_url" style="width:52px;height:68px;border-radius:4px" fit="cover" lazy>
              <template #error><div class="cover-ph">🎬</div></template>
            </el-image>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
        <el-table-column label="点赞" width="80">
          <template #default="{ row }">👍 {{ row.digg_count || 0 }}</template>
        </el-table-column>
        <el-table-column label="评论" width="80">
          <template #default="{ row }">💬 {{ row.comment_count || (row.downloaded ? 0 : '-') }}</template>
        </el-table-column>
        <el-table-column label="时长" width="60">
          <template #default="{ row }">{{ row.duration ? row.duration + 's' : '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template #default="{ row }">
            <el-tag size="small" :type="row.downloaded ? 'success' : 'info'">{{ row.downloaded ? '已下载' : '未下载' }}</el-tag>
            <el-tag size="small" :type="row.imported ? 'success' : 'default'" style="margin-left:4px">{{ row.imported ? '已入库' : '' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="290" fixed="right">
          <template #default="{ row }">
            <el-button v-if="row.video_url" size="small" text type="primary" tag="a" :href="row.video_url" target="_blank">📱 抖音</el-button>
            <el-button size="small" :loading="downloadingId === row.id" @click="download(row)">⬇ 下载</el-button>
            <el-button size="small" :type="row.imported ? 'success' : 'primary'" :disabled="!row.downloaded || row.imported" :loading="importingId === row.id" @click="importVid(row)">{{ row.imported ? '✅ 已入库' : '📦 入库' }}</el-button>
            <el-button v-if="row.downloaded && row.local_path" size="small" type="info" @click="copyPath(row)">📁</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 手机：卡片列表 -->
      <div v-if="isMobile" class="vid-cards">
        <div v-for="row in videos" :key="row.id" class="vid-card">
          <div class="vid-card-top">
            <el-image :src="row.cover_url" class="vid-cover" fit="cover" lazy>
              <template #error><div class="cover-ph-big">🎬</div></template>
            </el-image>
            <div class="vid-info">
              <div class="vid-title">{{ row.title || '无标题' }}</div>
              <div class="vid-meta">👍 {{ row.digg_count || 0 }} · 💬 {{ row.comment_count || 0 }} · ⏱ {{ row.duration ? row.duration + 's' : '-' }}</div>
              <div>
                <el-tag size="small" :type="row.downloaded ? 'success' : 'info'">{{ row.downloaded ? '已下载' : '未下载' }}</el-tag>
                <el-tag size="small" :type="row.imported ? 'success' : 'default'" style="margin-left:4px">{{ row.imported ? '已入库' : '' }}</el-tag>
              </div>
            </div>
          </div>
          <div class="vid-card-btns">
            <el-button v-if="row.video_url" size="small" text type="primary" tag="a" :href="row.video_url" target="_blank">📱 抖音</el-button>
            <el-button size="small" :loading="downloadingId === row.id" @click="download(row)">⬇ 下载</el-button>
            <el-button size="small" :type="row.imported ? 'success' : 'primary'" :disabled="!row.downloaded || row.imported" :loading="importingId === row.id" @click="importVid(row)">{{ row.imported ? '✅ 已入库' : '📦 入库' }}</el-button>
            <el-button v-if="row.downloaded && row.local_path" size="small" type="info" @click="copyPath(row)">📁</el-button>
          </div>
        </div>
        <el-empty v-if="!videos.length" description="暂无作品数据" />
      </div>
    </el-drawer>

    <!-- 拆解报告 -->
    <el-dialog v-model="reportVisible" title="📊 对标拆解报告" width="680px" top="5vh">
      <pre class="report">{{ reportText }}</pre>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'Benchmark',
  data() {
    return {
      accounts: [],
      accountsList: [],
      addVisible: false,
      saving: false,
      collectingId: 0,
      form: { account_id: 0, profile_url: '', nickname: '' },
      videoDrawer: false,
      currentAccount: null,
      videos: [],
      vLoading: false,
      downloadingId: 0,
      importingId: 0,
      reportVisible: false,
      reportText: '',
      isMobile: window.innerWidth < 768
    }
  },
  computed: {
    curAcct() {
      return this.accountsList.find((x) => x.id === this.form.account_id) || null
    }
  },
  created() {
    this.load()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    ownerName(id) {
      const x = this.accountsList.find((y) => y.id === id)
      return x ? x.nickname : `#${id}`
    },
    ownerOnline(id) {
      const x = this.accountsList.find((y) => y.id === id)
      return x ? !!x.is_logged_in : false
    },
    onResize() {
      this.isMobile = window.innerWidth < 768
    },
    fmt(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    async load() {
      try {
        const r = await api.get('/benchmark/accounts')
        this.accounts = r.accounts || []
        const a = await api.get('/accounts/')
        this.accountsList = (Array.isArray(a) ? a : (a.accounts || [])).map((x) => ({ id: x.id, nickname: x.nickname || x.username || ('账号' + x.id), is_logged_in: !!x.is_logged_in }))
        // 默认选中第一个已登录的账号
        if (!this.form.account_id || !this.accountsList.find((x) => x.id === this.form.account_id && x.is_logged_in)) {
          const logged = this.accountsList.find((x) => x.is_logged_in)
          this.form.account_id = logged ? logged.id : (this.accountsList[0] || {}).id || 0
        }
      } catch (e) {
        this.$message.error('加载失败: ' + e.message)
      }
    },
    openAdd() {
      const logged = this.accountsList.find((x) => x.is_logged_in)
      this.form = { account_id: logged ? logged.id : (this.accountsList[0]?.id || 0), profile_url: '', nickname: '' }
      this.addVisible = true
    },
    async save() {
      if (!this.form.profile_url.trim()) {
        this.$message.warning('请填主页链接')
        return
      }
      if (!this.form.account_id) {
        this.$message.warning('请先选择执行账号')
        return
      }
      const cur = this.accountsList.find((x) => x.id === this.form.account_id)
      if (cur && !cur.is_logged_in) {
        this.$message.warning(`账号「${cur.nickname}」未登录，采集将失败！请先到「账号管理」完成登录`)
        return
      }
      this.saving = true
      try {
        const r = await api.post('/benchmark/accounts', this.form)
        this.$message.success(r.resolved ? '已添加对标账号并解析链接 ✅（点「🔄 采集列表」开始抓取）' : '已添加对标账号 ✅（点「🔄 采集列表」开始抓取）')
        this.addVisible = false
        await this.load()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.saving = false
      }
    },
    async collect(a) {
      this.collectingId = a.id
      try {
        const r = await api.post(`/benchmark/accounts/${a.id}/collect`, {}, { timeout: 120000 })
        this.$message.success(r.detail || '采集完成')
        this.load()
        // 若当前正开着该账号的作品抽屉 → 刷新展示
        if (this.currentAccount && this.currentAccount.id === a.id) this.loadVideos()
      } catch (e) {
        this.$message.error('采集失败: ' + e.message)
      } finally {
        this.collectingId = 0
      }
    },
    async openVideos(a) {
      this.currentAccount = a
      this.videoDrawer = true
      this.vLoading = true
      try {
        const r = await api.get(`/benchmark/accounts/${a.id}/videos`)
        this.videos = r.videos || []
        if (!this.videos.length) {
          // 无数据 → 自动采集（首次打开）
          this.$message.info('该对标账号还没有作品数据，正在自动采集…（约30-60秒）')
          await this.collect(a)
        }
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.vLoading = false
      }
    },
    async loadVideos() {
      this.vLoading = true
      try {
        const r = await api.get(`/benchmark/accounts/${this.currentAccount.id}/videos`)
        this.videos = r.videos || []
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.vLoading = false
      }
    },
    async download(row) {
      this.downloadingId = row.id
      try {
        const r = await api.post(`/benchmark/videos/${row.id}/download`, {}, { timeout: 120000 })
        if (r.success) {
          this.$message.success(r.detail)
        } else {
          this.$message.error(r.detail)
        }
        this.loadVideos()
      } catch (e) {
        this.$message.error('下载失败: ' + e.message)
      } finally {
        this.downloadingId = 0
      }
    },
    copyPath(row) {
      try {
        navigator.clipboard.writeText(row.local_path)
        this.$message.success('已复制视频路径: ' + row.local_path)
      } catch (e) {
        this.$message.info('视频路径: ' + row.local_path)
      }
    },
    async importVid(row) {
      this.importingId = row.id
      try {
        const r = await api.post(`/benchmark/videos/${row.id}/import`, {}, { timeout: 120000 })
        if (r.success) {
          this.$message.success(r.detail)
          this.loadVideos()
        } else {
          this.$message.error(r.detail)
        }
      } catch (e) {
        this.$message.error('入库失败: ' + e.message)
      } finally {
        this.importingId = 0
      }
    },
    async analyze(a) {
      this.$message.info('正在生成拆解报告...')
      try {
        const r = await api.post(`/benchmark/accounts/${a.id}/analyze`)
        if (r.success) {
          this.reportText = r.report
          this.reportVisible = true
        } else {
          this.$message.error(r.detail || '暂无数据，请先采集')
        }
      } catch (e) {
        this.$message.error(e.message)
      }
    },
    async remove(a) {
      try {
        await this.$confirm(`删除对标账号「${a.nickname || a.profile_url}」及其作品数据？`, '确认删除', { type: 'warning' })
        await api.delete(`/benchmark/accounts/${a.id}`)
        this.$message.success('已删除')
        this.load()
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e.message || '删除失败')
      }
    }
  }
}
</script>

<style scoped>
.benchmark { padding: 4px; }
.head-card { margin-bottom: 12px; }
.head-row { display: flex; justify-content: space-between; align-items: center; }
.head-left h3 { margin: 0 0 4px; }
.sub { margin: 0; color: #909399; font-size: 12px; }
.acc-card { margin-bottom: 12px; }
.acc-head { display: flex; align-items: center; gap: 10px; }
.acc-name { font-weight: 600; font-size: 15px; }
.acc-meta { color: var(--el-text-color-secondary, #606266); font-size: 12px; margin-top: 2px; }
.acc-url { margin-top: 8px; font-size: 11px; color: #409eff; word-break: break-all; }
.acc-foot { margin-top: 6px; font-size: 11px; color: #909399; }
.acc-btns { margin-top: 10px; display: flex; gap: 4px; flex-wrap: wrap; }
.drawer-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.hint { font-size: 12px; color: #909399; }
.cover-ph { width: 52px; height: 68px; background: var(--el-fill-color, #f0f2f5); display: flex; align-items: center; justify-content: center; border-radius: 4px; font-size: 18px; }
.report { background: #0d1117; color: #7ee787; padding: 14px; border-radius: 8px; max-height: 62vh; overflow: auto; font-size: 13px; white-space: pre-wrap; line-height: 1.7; }
.vid-cards { display: flex; flex-direction: column; gap: 10px; padding-bottom: 20px; }
.vid-card { background: var(--el-bg-color-overlay, #fff); border: 1px solid var(--el-border-color-light, #ebeef5); border-radius: 8px; padding: 10px; }
.vid-card-top { display: flex; gap: 10px; }
.vid-cover { width: 92px; height: 122px; border-radius: 6px; flex-shrink: 0; }
.vid-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.vid-title { font-size: 14px; line-height: 1.4; font-weight: 500; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.vid-meta { font-size: 12px; color: var(--el-text-color-secondary, #606266); }
.vid-card-btns { margin-top: 8px; display: flex; gap: 6px; flex-wrap: wrap; }
.cover-ph-big { width: 92px; height: 122px; background: var(--el-fill-color, #f0f2f5); display: flex; align-items: center; justify-content: center; border-radius: 6px; font-size: 28px; }
@media (max-width: 767px) {
  .head-row { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
